import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { EmojiResource } from '@atlaskit/emoji/resource';

import { getEmojiProvider } from '@confluence/fabric-providers/entry-points/EmojiProvider';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';

import { createNavigateToObject } from '../utils/navigation';
import {
	showCreateWithRovoModalCloseFeedbackFlag,
	FeedbackVariant,
} from '../utils/showFeedbackFlag';

const CLOSE_MODAL_DELAY = 500;

export const useConversationAssistantConfig = ({
	assistanceServiceConfigHeaders = {},
	preventAbortRequestsOnStagingModalClose = false,
	preventAbortRequestsOnStandaloneChatClose = false,
}: {
	assistanceServiceConfigHeaders?: Record<string, string>;
	preventAbortRequestsOnStagingModalClose?: boolean;
	preventAbortRequestsOnStandaloneChatClose?: boolean;
}) => {
	const { cloudId, orgId, userId } = useSessionData();
	const emojiProvider = useMemo(() => {
		return getEmojiProvider(EmojiResource, {
			cloudId,
			userId,
		});
	}, [userId, cloudId]);

	return {
		displaySubmitFlag: true,
		siteId: cloudId,
		product: 'confluence',
		orgId,
		assistanceServiceConfig: {
			headers: assistanceServiceConfigHeaders,
			baseUrl: '/gateway/api/assist',
		},
		emojiProvider,
		userId: userId ?? undefined,
		preventAbortRequestsOnStagingModalClose,
		preventAbortRequestsOnStandaloneChatClose,
	};
};

export const useConversationAssistantUiConfig = ({ flags }: { flags: FlagsStateContainer }) => {
	const { push } = useRouteActions();
	const intl = useIntl();
	const { showModal } = useDialogs();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const navigateToObject = useCallback(
		async ({ baseUrl: baseUrlNav, path }: { baseUrl: string; path: string }) => {
			createNavigateToObject(push)({ baseUrl: baseUrlNav, path });
			// artifical delay before closing the modal gives the app a moment to transition to the created object while the modal is still open, this creates a smoother transition from modal to new object.
			// For non-SPA transitions, a full page reload will happen so this delay will be ignored
			await new Promise((r) => setTimeout(r, CLOSE_MODAL_DELAY));
		},
		[push],
	);

	const handleCloseSmartCreate = useCallback(() => {
		showCreateWithRovoModalCloseFeedbackFlag(
			flags,
			intl,
			showModal,
			FeedbackVariant.AFTER_AIFC_MODAL_CLOSE,
			undefined,
			createAnalyticsEvent,
		);
	}, [flags, intl, showModal, createAnalyticsEvent]);

	return {
		customHandlers: {
			handleSubmitSmartCreate: navigateToObject,
			handleCloseSmartCreate,
		},
	};
};
